/*
 * Native Possum — np-product.js
 * The product page's ONE selection state, plus the controls in the page markup
 * (colour + size buttons, thumbnails, main image, size/care panels, add to bag).
 * Reads the #npProductData JSON (main-product.liquid). Options are matched by
 * the positions found BY NAME there — never "option1 is the colour".
 *
 *   window.npProduct
 *     data                     the JSON block
 *     state                    { colour, size, sizeChosen }  (sizeChosen: the shopper
 *                              picked a size; the phone asks for one until then)
 *     variant()                the variant for the current state, or null
 *     setColour(colour, opts)  opts.source: 'swatch' | 'media' | 'phone' | 'url'
 *     setSize(size, opts)      also marks sizeChosen
 *     clearSize()              after a successful add (desktop shows no size picked)
 *     colourMediaIndex(colour) the photo to show for a colour (-1 if none)
 *     mediaChosen(index)       the shopper landed on a photo in any view (thumbnail,
 *                              swipe, dot, full-screen viewer): a photo of a colour picks
 *                              that colour (source 'media', so no view jumps); photos
 *                              with no colour leave it alone
 *     showMedia(index)         desktop/tablet main image shows a photo + mediaChosen
 *     detailsText()            "Navy - Size M" (the "You've selected" line)
 *     priceText()              the page price: the chosen variant's price, or "From $159"
 *                              while sizes are priced differently and none is picked yet
 *     sizePriceText(size)      " · $185" when sizes are priced differently (Bassinet vs
 *                              Cot), else '' — for size buttons/pills/Add to Bag text
 *     add(opts)                add to bag through npCart; resolves true / false
 *     truncateDescription(n)   short description for the phone (140) / tablet (190)
 *     watch(query, mount)      mount() runs while the media query matches and returns
 *                              its own unmount — views come and go as a phone or
 *                              iPad rotates, instead of deciding once at load
 *   document 'np:product:change'  detail { changed: ['colour'] | ['size'], source }
 *
 * Used by: np-product-mobile.js (≤767px), np-product-tablet.js (768–900px),
 *          np-product-lightbox-desktop.liquid.
 */
(function() {
  'use strict';

  var dataEl = document.getElementById('npProductData');
  if (!dataEl) return;
  var data = JSON.parse(dataEl.textContent);

  var ci = data.colourIndex;                 // null when there is no Colour option
  var si = data.sizeIndex;                   // null when there is no Size option
  var hasColourOption = ci !== null;

  var initial = data.variants.filter(function(v) { return v.id === data.initialVariant; })[0] || data.variants[0];
  var params = new URLSearchParams(window.location.search);

  var state = {
    colour: hasColourOption ? initial.options[ci] : (data.staticColour || ''),
    size: data.hasSize ? initial.options[si] : null,
    sizeChosen: data.hasSize && params.has('variant')
  };

  var mediaIndexById = {};
  data.media.forEach(function(m, i) { mediaIndexById[m.id] = i; });

  // ============================================
  // STATE
  // ============================================
  function variant() {
    return data.variants.filter(function(v) {
      if (hasColourOption && v.options[ci] !== state.colour) return false;
      if (data.hasSize && v.options[si] !== state.size) return false;
      return true;
    })[0] || null;
  }

  function emit(changed, source) {
    syncForm();
    syncUrl(source);
    document.dispatchEvent(new CustomEvent('np:product:change', {
      detail: { changed: changed, source: source || '' }
    }));
  }

  function setColour(colour, opts) {
    opts = opts || {};
    if (!hasColourOption) return;            // static (one-colour) bundles never change
    if (data.colours.indexOf(colour) === -1) return;
    state.colour = colour;
    emit(['colour'], opts.source);
  }

  function setSize(size, opts) {
    opts = opts || {};
    if (!data.hasSize || data.sizes.indexOf(size) === -1) return;
    state.size = size;
    state.sizeChosen = true;
    emit(['size'], opts.source);
  }

  function clearSize() {
    if (!data.hasSize) return;
    state.size = null;
    state.sizeChosen = false;
    emit(['size'], 'added');
  }

  // The colour's own photo (the variant's featured image), else its first tagged photo
  function colourMediaIndex(colour) {
    if (!colour) return -1;
    if (hasColourOption) {
      for (var i = 0; i < data.variants.length; i++) {
        var v = data.variants[i];
        if (v.options[ci] === colour && v.image && mediaIndexById[v.image] !== undefined) {
          return mediaIndexById[v.image];
        }
      }
    }
    for (var j = 0; j < data.media.length; j++) {
      if (data.media[j].colour === colour) return j;
    }
    return -1;
  }

  function detailsText() {
    var c = state.colour || '';
    var s = data.hasSize ? state.size : null;
    if (data.isBundle) {
      var label = hasColourOption ? data.sizeShortLabel : data.sizeLabel;
      return s ? c + ' - ' + label + ' Size ' + s : c;
    }
    if (s) return c ? c + ' - Size ' + s : 'Size ' + s;
    return c;
  }

  // Prices come from #npProductData (variant.price, sizePrices, priceMin), so the page,
  // phone sheet and sticky bars all read one rule.
  function priceText() {
    var v = variant();
    if (data.sizePrices && !state.sizeChosen) return 'From ' + window.npFormatMoney(data.priceMin);
    if (v) return window.npFormatMoney(v.price);
    return (data.priceVaries ? 'From ' : '') + window.npFormatMoney(data.priceMin);
  }

  function sizePriceText(size) {
    if (!data.sizePrices || data.sizePrices[size] === undefined) return '';
    return ' \u00b7 ' + window.npFormatMoney(data.sizePrices[size]);
  }

  function stockNotice() {
    if (data.productNotice) return data.productNotice;
    var v = variant();
    return (v && data.variantNotices[String(v.id)]) || null;
  }

  var form = document.getElementById('npProductForm');
  function syncForm() {
    var v = variant();
    if (form && v) form.elements.id.value = v.id;
  }

  // ?variant= is Shopify's own link format (Google Shopping uses it). Old ?colour= links still open.
  function syncUrl(source) {
    if (source === 'url') return;
    var v = variant();
    if (!v || data.variants.length < 2) return;
    var url = new URL(window.location.href);
    url.searchParams.set('variant', v.id);
    url.searchParams.delete('colour');
    history.replaceState(history.state, '', url);
  }

  // ============================================
  // LAYOUT WATCHER
  // ============================================
  function watch(query, mount) {
    var mql = window.matchMedia(query);
    var unmount = null;
    function apply() {
      if (mql.matches && !unmount) {
        unmount = mount() || function() {};
      } else if (!mql.matches && unmount) {
        unmount();
        unmount = null;
      }
    }
    mql.addEventListener('change', apply);
    apply();
  }

  // ============================================
  // DESCRIPTION (phone + small tablet show a shortened one)
  // ============================================
  var descWrapper = document.querySelector('.np-description-wrapper');

  function truncateDescription(limit, minBreak) {
    if (!descWrapper) return;
    var expanded = descWrapper.querySelector('.np-description-expanded');
    var collapsed = descWrapper.querySelector('.np-description-collapsed');
    if (!expanded || !collapsed) return;
    var plain = expanded.textContent.trim();
    var cut = limit;
    if (plain.length > cut) {
      var space = plain.lastIndexOf(' ', cut);
      if (space > minBreak) cut = space;
    }
    collapsed.textContent = plain.substring(0, cut).trim();
    // Hidden by the FOUC rule in main-product.liquid until the text is in
    descWrapper.querySelectorAll('.np-description-ellipsis, .np-description-chevron').forEach(function(el) {
      el.style.visibility = 'visible';
    });
  }

  // ============================================
  // SHORT VIEW (phone + small tablet, ≤900px)
  // One description and one care panel serve every width. Here the description
  // is a "… ›" button that expands it; care is shown open under it (its toggle is
  // hidden) once the description is expanded. Desktop: full description, care
  // accordion. Rotation-safe: unmount puts the desktop state back.
  // ============================================
  var descBox = document.querySelector('.product-description');
  var careToggle = document.querySelector('.care-guide-toggle');
  var careContent = document.getElementById('careGuideContent');

  function setCareOpen(open) {
    if (!careToggle || !careContent) return;
    careContent.classList.toggle('active', open);
    careToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    var arrow = careToggle.querySelector('.care-arrow');
    if (arrow) arrow.style.transform = open ? 'rotate(90deg)' : '';
  }

  function isExpanded() {
    return !!descBox && descBox.classList.contains('expanded');
  }

  function expandDescription(e) {
    if (isExpanded()) return;
    e.preventDefault();
    descBox.classList.add('expanded');
    descWrapper.classList.add('expanded');
    descWrapper.removeAttribute('role');
    descWrapper.removeAttribute('tabindex');
    descWrapper.removeAttribute('aria-expanded');
  }

  function onDescKey(e) {
    if (e.key === 'Enter' || e.key === ' ') expandDescription(e);
  }

  watch('(max-width: 900px)', function mountShortView() {
    if (descWrapper && !isExpanded()) {
      descWrapper.setAttribute('role', 'button');
      descWrapper.setAttribute('tabindex', '0');
      descWrapper.setAttribute('aria-expanded', 'false');
    }
    if (descWrapper) {
      descWrapper.addEventListener('click', expandDescription);
      descWrapper.addEventListener('keydown', onDescKey);
    }
    setCareOpen(true);
    return function unmountShortView() {
      if (descWrapper) {
        descWrapper.removeEventListener('click', expandDescription);
        descWrapper.removeEventListener('keydown', onDescKey);
        descWrapper.removeAttribute('role');
        descWrapper.removeAttribute('tabindex');
        descWrapper.removeAttribute('aria-expanded');
      }
      setCareOpen(false);
    };
  });

  // ============================================
  // PAGE CONTROLS (desktop + tablet markup; the phone views drive the same state)
  // ============================================
  var colourButtons = Array.prototype.slice.call(document.querySelectorAll('.colour-option-container'));
  var sizeButtons = Array.prototype.slice.call(document.querySelectorAll('.size-option'));
  var thumbnails = Array.prototype.slice.call(document.querySelectorAll('.thumbnail'));
  var mainImage = document.getElementById('mainImage');
  var showMoreBtn = document.getElementById('showMoreColoursBtn');

  function expandColours() {
    colourButtons.forEach(function(b) { b.classList.remove('hidden'); });
    if (showMoreBtn) {
      showMoreBtn.classList.add('hidden');
      showMoreBtn.setAttribute('aria-expanded', 'true');
    }
  }

  function setActiveThumb(index) {
    thumbnails.forEach(function(t, i) {
      t.classList.toggle('active', i === index);
      if (i === index) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  // Fade the main image to a photo (200ms out, swap, back in)
  function showMainMedia(index, opts) {
    if (!mainImage) return;
    var m = data.media[index];
    mainImage.style.opacity = '0';
    setTimeout(function() {
      if (m) {
        mainImage.src = m.url;
        mainImage.style.objectPosition = m.fx + '% ' + m.fy + '%';
        mainImage.alt = m.alt || data.title;
      }
      mainImage.style.opacity = '1';
    }, 200);
    if (m) {
      setActiveThumb(index);
      if (opts && opts.scroll && thumbnails[index]) {
        thumbnails[index].scrollIntoView({ behavior: window.npMotion.scroll(), block: 'center' });
      }
    }
  }

  function renderColour() {
    colourButtons.forEach(function(b) {
      var on = b.getAttribute('data-colour') === state.colour;
      b.classList.toggle('selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on && b.classList.contains('hidden')) expandColours();
    });
  }

  function renderSize() {
    sizeButtons.forEach(function(b) {
      var on = b.getAttribute('data-size') === state.size;
      b.classList.toggle('selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  function renderDetails() {
    var el = document.getElementById('selectedDetails');
    if (el) el.textContent = detailsText();
  }

  function renderPrice() {
    var el = document.getElementById('productPrice');
    if (el) el.textContent = priceText();
  }

  function renderStockNotice() {
    var text = stockNotice();
    var box = document.getElementById('stockNotice');
    var msg = document.getElementById('stockNoticeMessage');
    if (!box || !msg) return;
    if (text) msg.textContent = text;
    box.classList.toggle('hidden', !text);
  }

  colourButtons.forEach(function(b) {
    b.addEventListener('click', function() {
      setColour(b.getAttribute('data-colour'), { source: 'swatch' });
    });
  });

  sizeButtons.forEach(function(b) {
    b.addEventListener('click', function() {
      setSize(b.getAttribute('data-size'), { source: 'button' });
    });
  });

  if (showMoreBtn) showMoreBtn.addEventListener('click', expandColours);

  // The one rule for every photo view: landing on a photo of a colour picks it
  function mediaChosen(index) {
    var colour = data.media[index] && data.media[index].colour;
    if (colour && colour !== state.colour) setColour(colour, { source: 'media' });
  }

  function showMedia(index) {
    showMainMedia(index, { scroll: true });
    mediaChosen(index);
  }

  thumbnails.forEach(function(t) {
    t.addEventListener('click', function() {
      showMedia(parseInt(t.getAttribute('data-media-index'), 10));
    });
  });

  document.addEventListener('np:product:change', function(e) {
    var changed = e.detail.changed;
    if (changed.indexOf('colour') !== -1) {
      renderColour();
      if (e.detail.source !== 'media') {
        var index = colourMediaIndex(state.colour);
        if (index === -1) {
          showMainMedia(-1);
        } else {
          showMainMedia(index);
        }
      }
    }
    if (changed.indexOf('size') !== -1) renderSize();
    renderDetails();
    renderPrice();
    renderStockNotice();
  });

  // ---- Size guide + care panels (open/close; npSyncDisclosure handles inert) ----
  function bindPanel(toggle, contentId, arrowSelector, onToggle) {
    if (!toggle) return;
    toggle.addEventListener('click', function() {
      var content = document.getElementById(contentId);
      if (!content) return;
      var opening = !content.classList.contains('active');
      content.classList.toggle('active', opening);
      toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
      var arrow = toggle.querySelector(arrowSelector);
      if (arrow) arrow.style.transform = opening ? 'rotate(90deg)' : 'rotate(0deg)';
      if (onToggle) onToggle(opening);
    });
  }

  bindPanel(document.querySelector('.size-guide-toggle'), 'sizeGuideContent', '.guide-arrow', function(opening) {
    var wrapper = document.querySelector('.size-selection-wrapper');
    if (!wrapper) return;
    if (opening) wrapper.classList.add('size-guide-expanded');
    else setTimeout(function() { wrapper.classList.remove('size-guide-expanded'); }, 400);
  });
  bindPanel(document.querySelector('.care-guide-toggle'), 'careGuideContent', '.care-arrow');

  // ---- Swatch photos (or the colour's dot, for a colour with no photo yet) + first photo of each colour (no white flash on change) ----
  colourButtons.forEach(function(b) {
    var name = b.getAttribute('data-colour');
    var url = data.swatches[name];
    var holder = b.querySelector('.colour-swatch');
    if (!holder) return;
    if (!url) {
      if (data.dots && data.dots[name]) holder.setAttribute('style', data.dots[name]); // no photo yet: its dot
      return;
    }
    var img = document.createElement('img');
    img.src = url;
    img.alt = name;
    img.width = 150;
    img.height = 150;
    holder.appendChild(img);
  });

  // After the page has loaded, so they never compete with what is on screen
  window.addEventListener('load', function() {
    data.colours.forEach(function(colour) {
      var index = colourMediaIndex(colour);
      if (index > -1) (new Image()).src = data.media[index].url;
    });
  });

  // ---- Thumbnail strip: overflow fade + scroll arrow ----
  var strip = document.getElementById('thumbnailStrip');
  if (strip) {
    var stripWrapper = document.querySelector('.thumbnail-strip-wrapper');
    var indicator = document.getElementById('scrollIndicator');
    var updateStrip = function() {
      var hasScroll = strip.scrollHeight > strip.clientHeight;
      var atBottom = strip.scrollHeight - strip.scrollTop <= strip.clientHeight + 10;
      if (stripWrapper) {
        stripWrapper.classList.toggle('has-overflow', hasScroll);
        stripWrapper.classList.toggle('at-bottom', atBottom);
      }
      if (indicator) indicator.classList.toggle('visible', hasScroll && !atBottom);
    };
    strip.addEventListener('scroll', updateStrip);
    window.addEventListener('resize', updateStrip);
    updateStrip();
  }

  // ============================================
  // ADD TO BAG
  // ============================================
  // Errors sit before .btn-add-to-bag where it's visible (tablet / desktop),
  // else fixed above the phone's sticky bar. announce: true when npCart
  // hasn't already spoken it.
  function showAddError(message, announce) {
    document.querySelectorAll('[data-np-add-error]').forEach(function(el) { el.remove(); });
    var box = document.createElement('div');
    box.className = 'error-message';
    box.setAttribute('data-np-add-error', '');
    if (announce) box.setAttribute('role', 'alert');
    box.textContent = message;

    var button = document.querySelector('.btn-add-to-bag');
    var stickyBar = document.getElementById('npStickyBar');
    if (button && button.getClientRects().length) {
      button.insertAdjacentElement('beforebegin', box);
    } else if (stickyBar && stickyBar.getClientRects().length) {
      box.classList.add('np-sticky-error');
      stickyBar.insertAdjacentElement('beforebegin', box);
    } else if (button) {
      button.insertAdjacentElement('beforebegin', box);
    }
    window.npMessage.hold(box, function() { box.remove(); });
  }

  function missingMessage() {
    var hasColour = hasColourOption || !!data.staticColour;
    if (data.hasSize && hasColour) return 'Please select a colour and size before adding to bag';
    if (data.hasSize) return 'Please select a size before adding to bag';
    return 'Please select a colour before adding to bag';
  }

  // Resolves true when added. The bag opens itself (np:cart:updated).
  function add(opts) {
    opts = opts || {};
    document.querySelectorAll('[data-np-add-error]').forEach(function(el) { el.remove(); });

    var v = variant();
    if (!v) {
      showAddError(missingMessage(), true);
      return Promise.resolve(false);
    }

    var item = { id: v.id, quantity: 1 };
    var notice = stockNotice();
    if (notice) item.properties = { '_stock_notice': notice };

    var buttons = [document.querySelector('.btn-add-to-bag'), document.getElementById('npStickyAddBtn')].filter(Boolean);
    // Focus returns here when the bag closes: the button used, else the visible one
    var trigger = opts.trigger || buttons.filter(function(b) { return b.getClientRects().length; })[0] || null;

    return window.npCart.add([item], { control: buttons, trigger: trigger }).then(function() {
      if (data.hasSize) clearSize();
      return true;
    }, function(error) {
      // npCart has already announced it
      showAddError((error && error.message) || 'Error adding to bag. Please try again.', false);
      return false;
    });
  }

  if (form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      add({ trigger: e.submitter || null });
    });
  }

  // ============================================
  // API
  // ============================================
  window.npProduct = {
    data: data,
    state: state,
    variant: variant,
    setColour: setColour,
    setSize: setSize,
    clearSize: clearSize,
    colourMediaIndex: colourMediaIndex,
    mediaChosen: mediaChosen,
    showMedia: showMedia,
    detailsText: detailsText,
    priceText: priceText,
    sizePriceText: sizePriceText,
    add: add,
    truncateDescription: truncateDescription,
    watch: watch
  };

  // Old ?colour= links (before ?variant=): pick that colour once the views are up
  var linkedColour = params.get('colour');
  if (linkedColour && !params.has('variant') && linkedColour !== state.colour) {
    document.addEventListener('DOMContentLoaded', function() {
      setColour(linkedColour, { source: 'url' });
    });
  }
})();
